/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { User } from "@coworker/apprestructured/src/shared/types/user";
import PersonIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PersonIconPath";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import { useTranslation } from "@coworker/locales";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { useCallback, useState } from "react";
import { AssignToModal, OnConfirmClickResult } from "./AssignToModal";

type AssignToProps = {
  disabled: boolean;
  assignedUserId: string | null;
  assignedTeamId: string | null;
  description: string;
  searchQuery: string;
  isTeamsLoading: boolean;
  myTeamId: string;
  isUsersLoading: boolean;
  teams: StoreTeam[];
  users: User[];
  isError: boolean;
  mostRecentAssignedUser?: User | null;
  mostRecentAssignedTeam?: StoreTeam | null;
  onConfirmClick: (result: OnConfirmClickResult) => void;
  onSearchQueryChange: (query: string) => void;
};

export const AssignTo = ({
  disabled,
  assignedUserId,
  assignedTeamId,
  description,
  searchQuery,
  isTeamsLoading,
  myTeamId,
  isUsersLoading,
  teams,
  users,
  isError,
  mostRecentAssignedUser,
  mostRecentAssignedTeam,
  onConfirmClick,
  onSearchQueryChange,
}: AssignToProps) => {
  const [showModal, setShowModal] = useState(false);

  const { t } = useTranslation();

  const handleListViewItemClick = useCallback(() => {
    setShowModal(true);
    onSearchQueryChange("");
  }, [onSearchQueryChange]);

  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
    }, [])
  );

  const handleEnableSaveGuard = useCallback(() => {
    enableSaveGuard();
  }, [enableSaveGuard]);

  const handleModalConfirmClick = useCallback(
    (result: OnConfirmClickResult) => {
      onConfirmClick(result);
      setShowModal(false);
      disableSaveGuard();
    },
    [onConfirmClick, disableSaveGuard]
  );

  return (
    <>
      <ListItem
        title={t("assignString")}
        icon={PersonIconPath}
        onClick={handleListViewItemClick}
        disabled={disabled}
        addOn={
          isError ? <ErrorAddOn>{t("pleaseFillInString")}</ErrorAddOn> : null
        }
        description={description}
        emphasised={!!assignedTeamId || !!assignedUserId}
        testId="assign-to-list-item"
      />

      <AssignToModal
        key={`assign-to-modal-${assignedTeamId ?? "none"}-assign-to-user-${assignedUserId ?? "none"}`}
        visible={showModal}
        onClose={handleCloseClick}
        searchQuery={searchQuery}
        isTeamsLoading={isTeamsLoading}
        isUsersLoading={isUsersLoading}
        teams={teams}
        users={users}
        myTeamId={myTeamId}
        mostRecentAssignedUser={mostRecentAssignedUser}
        mostRecentAssignedTeam={mostRecentAssignedTeam}
        initialAssignedUserId={assignedUserId}
        initialAssignedTeamId={assignedTeamId}
        onSearchQueryChange={onSearchQueryChange}
        onUserIdChange={handleEnableSaveGuard}
        onTeamIdChange={handleEnableSaveGuard}
        onConfirmClick={handleModalConfirmClick}
      />
    </>
  );
};
